import { describeTarget } from './naming.ts'
import { describeContext } from './context.ts'
import { MAX_SELECTION_CHARS } from '../bundle.ts'
import type { LoggableEvent } from '../recorder.ts'

export interface SelectionOptions {
  document: Document
  window: Window
  log: (event: LoggableEvent) => void
  /** Wait for the selection to stop changing before logging it. */
  settleMs?: number
}

const IGNORE_SELECTOR = '[data-sat-ignore]'
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
const DEFAULT_SETTLE_MS = 400

/**
 * Logs text the user selects on the page. This is the one place record text is kept (≤200 chars),
 * so it never reads from inputs, textareas or editable regions, where typed data lives (EC27).
 */
export function startSelection({ document, window, log, settleMs = DEFAULT_SETTLE_MS }: SelectionOptions): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined
  let lastText = ''

  const read = () => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return
    const anchor = selection.anchorNode
    const element = anchor instanceof Element ? anchor : anchor?.parentElement
    if (!element || element.closest(IGNORE_SELECTOR) || element.closest(EDITABLE_SELECTOR)) return
    const active = document.activeElement
    if (active && active.matches(EDITABLE_SELECTOR) && !(active === document.body)) return
    const text = selection.toString().trim().slice(0, MAX_SELECTION_CHARS)
    if (text === '' || text === lastText) return
    lastText = text
    log({ type: 'selection', name: describeTarget(element).name, text, ...describeContext(element) })
  }

  const onChange = () => {
    clearTimeout(timer)
    timer = setTimeout(read, settleMs)
  }

  document.addEventListener('selectionchange', onChange)
  return () => {
    clearTimeout(timer)
    document.removeEventListener('selectionchange', onChange)
  }
}
